import { observer } from "mobx-react-lite";
import React, { useCallback, useState } from "react";
import { getVideoContainer } from "../../helpers/elementBoundsTracker";
import { ReactComponent as ConnectIcon } from "../../icons-and-animations/layer-inspector/connect.svg";
import { useMst } from "../../stores/main";
import type { ElementBounds } from "./TransformHandles/types";
import { useElementBounds } from "./TransformHandles/hooks/useElementBounds";

const getViewportPosition = (bounds: ElementBounds) => {
  const containerRect = getVideoContainer()?.getBoundingClientRect();
  const position = bounds.corners.ne;

  if (!containerRect) {
    return position;
  }

  return {
    x: containerRect.left + position.x,
    y: containerRect.top + position.y,
  };
};

export const LayerConnectorMenuButton = observer(() => {
  const {
    playerStore: {
      layerInspectorAvailable,
      layerInspectorContextMenu,
      layerInspectorEnabled,
      layerInspectorMode,
      layerInspectorPrimarySelection,
      layerInspectorSelections,
      showLayerInspectorContextMenu,
    },
  } = useMst();
  const [bounds, setBounds] = useState<ElementBounds | null>(null);
  const enabled =
    layerInspectorAvailable &&
    layerInspectorEnabled &&
    layerInspectorMode === "connect" &&
    Boolean(layerInspectorPrimarySelection);

  useElementBounds({
    selection: layerInspectorPrimarySelection,
    enabled,
    onBoundsChange: setBounds,
    isGestureActive: false,
  });

  const handleOpenMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      if (!layerInspectorPrimarySelection) return;

      const buttonRect = event.currentTarget.getBoundingClientRect();
      const clientX = buttonRect.right + 6;
      const clientY = buttonRect.top;
      const selectionList = layerInspectorSelections.length
        ? layerInspectorSelections.slice()
        : [layerInspectorPrimarySelection];

      showLayerInspectorContextMenu(
        {
          clientX,
          clientY,
          pageX: clientX + window.scrollX,
          pageY: clientY + window.scrollY,
        },
        selectionList,
        layerInspectorPrimarySelection
      );
    },
    [
      layerInspectorPrimarySelection,
      layerInspectorSelections,
      showLayerInspectorContextMenu,
    ]
  );

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.stopPropagation();
    },
    []
  );

  if (!enabled || !bounds) {
    return null;
  }

  const position = getViewportPosition(bounds);

  return (
    <button
      aria-expanded={Boolean(layerInspectorContextMenu)}
      aria-haspopup="menu"
      aria-label="Open connector menu"
      className="layer-connector-menu-button"
      data-layer-inspector-keep-selection
      onClick={handleOpenMenu}
      onPointerDown={handlePointerDown}
      style={{
        left: position.x,
        top: position.y,
      }}
      title="Connect layer"
      type="button"
    >
      <ConnectIcon aria-hidden="true" />
    </button>
  );
});
